import { Avatar, Box, Button, Collapse, IconButton, Menu, MenuItem, Popover, TextField, TextFieldProps, Tooltip } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import DotIcon from '@material-ui/icons/FiberManualRecord';
import GlobeIcon from '@material-ui/icons/Language';
import MenuIcon from '@material-ui/icons/Menu';
import OpenInNew from '@material-ui/icons/OpenInNew';
import SearchIcon from '@material-ui/icons/Search';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import AutoComplete from '../components/AutoComplete';
import useConfirmDialog from '../hooks/useConfirmDialog';
import components, { IRouteProps, RouteNames } from '../pages/config';
import api from '../services/api/base';
import session from '../services/session';
import settings from '../services/settings';
import GlobalLoadingBar from './GlobalLoadingBar';
import MiniVariantDrawer from './MiniVariantDrawer';
import SettingDropdownMenu from './SettingDropdownMenu';
import useStyles from './styles';

interface ILinkPropsBase {
  component: Link | 'a' | 'li';
  to?: string;
  href?: string;
  target?: string;
}

interface IInternalLinkProps extends ILinkPropsBase {
  component: Link;
  to: string;
  href?: undefined;
  target?: undefined;
}

interface IExternalLinkProps extends ILinkPropsBase {
  component: 'a';
  to?: undefined;
  href: string;
  target: string;
}

const Default = (): JSX.Element => {
  return <div />;
};

const NotFound = (): JSX.Element => {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 120,
      }}
    >
      <img src="https://radishfiction.s3-us-west-1.amazonaws.com/static/admin_404.png" width="379" height="278" />
    </div>
  );
};

const TemporaryCrewExternalUrl = settings.getCrewURI();

const routes = components
  .filter(({ routeProps, nestedMenus }) => nestedMenus || (routeProps && !routeProps.externalLink))
  .map(({ routeProps, nestedMenus }, index) => {
    const routes = [];

    if (routeProps && routeProps.component) {
      routes.push(<Route key={index} path={routeProps.path} exact={routeProps.exact} component={routeProps.component} />);
    }

    if (nestedMenus) {
      nestedMenus
        .filter(({ routeProps }) => routeProps && !routeProps.externalLink)
        .forEach(
          ({ routeProps }, nestedIndex) =>
            routeProps &&
            routes.push(<Route key={`${index}-${nestedIndex}`} path={routeProps.path} exact={routeProps.exact} component={routeProps.component} />),
        );
    }

    return routes;
  });

const MainLayout = (): JSX.Element => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const TimezoneList = useMemo((): string[] => settings.getTimezoneNameList(), []);

  const [timezone, setTimezone] = useState<string>(settings.getTimezone() as string);
  const [drawerShrink, setDrawerShrink] = useState<boolean>(settings.getDrawerShrink());
  const [currentExpandedMenuNames, setCurrentExpandedMenuNames] = useState<Set<string>>(new Set<string>());

  const [settingMenuAnchorEl, setSettingMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [timezoneMenuAnchorEl, setTimezoneMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);

  const confirm = useConfirmDialog();

  const timezoneAbbr = settings.getTimezoneAbbr();

  useEffect(() => {
    setCurrentExpandedMenuNames(ps => {
      const cloned = new Set(ps);
      const parentOfNestedComponent = components.find(c => c.nestedMenus?.find(nm => nm.routeProps?.path === location.pathname));

      if (parentOfNestedComponent?.menuProps?.name) {
        cloned.add(parentOfNestedComponent.menuProps.name);
      }

      return cloned;
    });
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    const value = !drawerShrink;

    settings.setDrawerShrink(value);
    setDrawerShrink(value);
  };

  const currentSession = session.get();

  const signout = () => {
    confirm.open({
      title: 'Sign Out',
      text: 'Are you sure?',
      onProceed: () => {
        session.remove();
        api.setAccessToken('');
        window.location.href = '/';
      },
    });
  };

  const getLinkProps = (routeProps?: IRouteProps): IInternalLinkProps | IExternalLinkProps | ILinkPropsBase => {
    if (!routeProps) {
      return { component: 'li' };
    }

    if (routeProps.externalLink) {
      return {
        component: 'a',
        href: routeProps.externalLink,
        target: '_blank',
      };
    }

    return {
      component: Link,
      to: routeProps.path || '',
    } as IInternalLinkProps;
  };

  const handleTimezoneClick = (event: React.MouseEvent<HTMLLIElement>) => {
    const selectedTimezone = event.currentTarget.getAttribute('value');

    if (selectedTimezone) {
      setTimezone(selectedTimezone);
      settings.setTimezone(selectedTimezone);
      setTimezoneMenuAnchorEl(null);
    }
  };

  const renderDrawerContent = () => (
    <div>
      <div className={classes.toolbar} />
      <List>
        {components.map(({ menuProps, routeProps, nestedMenus }) => {
          if (!menuProps || menuProps.unexposed) return;

          const { name, IconComponent } = menuProps;
          const visibleNestedMenus = nestedMenus?.filter(({ menuProps }) => !menuProps?.unexposed);
          const isCurrentMenu = routeProps && routeProps.path && location.pathname.indexOf(routeProps.path.toString()) === 0;

          return (
            <Fragment key={name}>
              <ListItem<any>
                button
                {...getLinkProps(routeProps)}
                className={isCurrentMenu ? classes.currentMenu : undefined}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (!routeProps) {
                    e.preventDefault();
                    setCurrentExpandedMenuNames(ps => {
                      const cloned = new Set(ps);
                      if (cloned.has(name)) {
                        cloned.delete(name);
                      } else {
                        cloned.add(name);
                      }

                      return cloned;
                    });
                  }
                }}
              >
                {IconComponent && (
                  <ListItemIcon>
                    <IconComponent />
                  </ListItemIcon>
                )}

                <ListItemText primary={name} />
                {visibleNestedMenus && visibleNestedMenus.length > 0 && (currentExpandedMenuNames.has(name) ? <ExpandLess /> : <ExpandMore />)}
                {routeProps?.externalLink && <OpenInNew style={{ fontSize: 16, marginRight: 3 }} color="disabled" />}
              </ListItem>

              {visibleNestedMenus && (
                <Collapse in={currentExpandedMenuNames.has(name)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {visibleNestedMenus.map(({ menuProps, routeProps }) => {
                      if (!menuProps || menuProps.unexposed) return;

                      const { name } = menuProps;
                      const isCurrentMenu = routeProps && routeProps.path && location.pathname.indexOf(routeProps.path.toString()) === 0;

                      return (
                        <ListItem<any>
                          button
                          key={name}
                          {...getLinkProps(routeProps)}
                          className={`${classes.nested} ${isCurrentMenu ? classes.currentMenu : ''}`}
                        >
                          {drawerShrink && <DotIcon color="disabled" className={classes.dot} />}
                          <ListItemText primary={name} className={classes.nextItemText} />
                          {routeProps?.externalLink && <OpenInNew style={{ fontSize: 16, marginRight: 3 }} color="disabled" />}
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Fragment>
          );
        })}
      </List>
      <Divider />
    </div>
  );

  return (
    <div className={classes.root}>
      <GlobalLoadingBar />
      <CssBaseline />

      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar disableGutters className={classes.toolbarGutters}>
          <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} className={classes.menuButton}>
            <MenuIcon />
          </IconButton>
          <Box display="flex" flexDirection="row" alignItems="center" width="100%">
            <Box display="flex" flex="1" alignItems="baseline">
              <Typography variant="h4" noWrap style={{ fontFamily: 'Damion' }}>
                Radish
              </Typography>
              <Typography variant="caption" noWrap style={{ fontWeight: 'bold' }}>
                &nbsp;&nbsp;Admin
              </Typography>
              {process.env.NODE_ENV !== 'production' && (
                <Box
                  display="flex"
                  paddingLeft="3px"
                  paddingRight="3px"
                  borderRadius="3px"
                  border="solid 1px #fff"
                  marginLeft="8px"
                  marginRight="8px"
                  height="18px"
                  alignItems="center"
                  whiteSpace="nowrap"
                >
                  <Typography variant="overline">{`DEV-${settings.getVersion()}`}</Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" alignItems="center">
              <div className={classes.search}>
                <div className={classes.searchIcon}>
                  <SearchIcon />
                </div>
                <AutoComplete
                  onSelect={({ id, type }) => {
                    if (type === 'story') {
                      window.open(`${TemporaryCrewExternalUrl}/stories/${id}`, '_blank', 'noopener noreferrer');
                    } else if (type === 'user') {
                      window.open(`${TemporaryCrewExternalUrl}/users/${id}`, '_blank', 'noopener noreferrer');
                    } else if (type === 'penname') {
                      history.push(`${RouteNames.Penname}/v/${id}`);
                    }
                  }}
                  render={(props: TextFieldProps) => {
                    const { InputProps, ...restProps } = props;
                    return (
                      <TextField
                        placeholder="Universal Search"
                        InputProps={{
                          classes: {
                            root: classes.inputRoot,
                            input: classes.inputInput,
                          },
                          ...InputProps,
                        }}
                        {...restProps}
                      />
                    );
                  }}
                />
              </div>
              <Box display="flex" alignItems="center">
                <Tooltip title={`All times shown are in ${timezoneAbbr}.`} placement="bottom" arrow>
                  <Button
                    style={{ color: '#fff' }}
                    onClick={event => {
                      setTimezoneMenuAnchorEl(timezoneMenuAnchorEl ? null : event.currentTarget);
                    }}
                  >
                    <GlobeIcon />
                    <Typography variant="button" style={{ marginLeft: 4, marginRight: 4 }}>
                      {timezoneAbbr}
                    </Typography>
                  </Button>
                </Tooltip>
              </Box>
              <Menu
                anchorEl={timezoneMenuAnchorEl}
                keepMounted
                open={Boolean(timezoneMenuAnchorEl)}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                onClose={() => {
                  setTimezoneMenuAnchorEl(null);
                }}
              >
                {TimezoneList.map((tz: string, idx: number) => (
                  <MenuItem
                    button
                    value={idx === 0 ? settings.getDefaultTimezoneName() : tz}
                    key={`${tz}-${idx}`}
                    onClick={handleTimezoneClick}
                    selected={tz === timezone}
                  >
                    {tz}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Settings" placement="bottom" arrow>
                <IconButton
                  color="inherit"
                  onClick={event => {
                    setSettingMenuAnchorEl(settingMenuAnchorEl ? null : event.currentTarget);
                  }}
                >
                  <SettingsApplicationsIcon />
                </IconButton>
              </Tooltip>
              <Popover
                id={'settingPopper'}
                open={Boolean(settingMenuAnchorEl)}
                anchorEl={settingMenuAnchorEl}
                onClose={() => setSettingMenuAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <SettingDropdownMenu />
              </Popover>

              <IconButton
                color="inherit"
                onClick={event => {
                  setUserMenuAnchorEl(userMenuAnchorEl ? null : event.currentTarget);
                }}
              >
                {currentSession?.thumbUrl ? (
                  <Avatar alt={currentSession?.username} src={currentSession?.thumbUrl} className={classes.avatar} />
                ) : (
                  <AccountCircleIcon />
                )}
              </IconButton>
              <Menu
                anchorEl={userMenuAnchorEl}
                keepMounted
                open={Boolean(userMenuAnchorEl)}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                onClose={() => {
                  setUserMenuAnchorEl(null);
                }}
              >
                <ListItem>
                  {currentSession?.username && (
                    <Typography variant="caption" color={'primary'}>
                      {`You logged as ${currentSession.username}.`}
                    </Typography>
                  )}
                </ListItem>
                <MenuItem onClick={signout}>Sign out</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <MiniVariantDrawer shrink={drawerShrink}>{renderDrawerContent()}</MiniVariantDrawer>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        <Switch>
          {routes}
          <Route exact path="/" component={Default} />
          <Route component={NotFound} />
        </Switch>
      </main>
    </div>
  );
};

export default MainLayout;
